import type { ReactElement } from 'react'
import { useState } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { Button, DashedButton } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { useHoldsThroughCompany, usePrivateHoldings, useSettings } from '~/state/queries'
import type { TaxRateDraft } from '~/state/tax'
import {
  draftFromVersion,
  nextDraftAfter,
  refuseTaxRateDrafts,
  sortDrafts,
  toTaxRateVersion,
  useSaveTaxRates,
  useTaxRates,
} from '~/state/tax'

const CSS = `
.nw-tax p { margin: 0 0 var(--nw-space-3); }
.nw-tax .nw-lede { color: var(--nw-body-ink); font-size: var(--nw-text-cell); max-width: 70ch; }
.nw-tax ul {
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-md);
  list-style: none; margin: var(--nw-space-4) 0 var(--nw-space-3); padding: 0;
}
.nw-tax li { border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); padding: var(--nw-space-3); }
.nw-tax li:last-child { border-bottom: 0; }
.nw-tax .nw-version-head { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-3); }
.nw-tax .nw-version-head .nw-btn { margin-left: auto; }
.nw-tax .nw-fields { display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-3); }
.nw-tax label { display: block; }
.nw-tax label > span { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-tax .nw-unit { align-items: center; display: flex; gap: var(--nw-space-1); }
.nw-tax .nw-unit span[aria-hidden] { color: var(--nw-muted); font-size: var(--nw-text-cell); letter-spacing: 0; text-transform: none; }
.nw-tax input[type='number'] { text-align: right; width: 96px; }
.nw-tax input[type='date'] { width: 160px; }
.nw-tax .nw-threshold input { width: 140px; }
.nw-tax .nw-decide { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); }
.nw-tax .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0; }
.nw-tax .nw-unsaved { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0; }
.nw-tax .nw-disclaimer { color: var(--nw-muted); font-size: var(--nw-text-chip); max-width: 70ch; }
`

/**
 * What the household is charged for taking money out of a company it owns, and
 * the only place it can say so.
 *
 * Every household is created holding the same Danish versions, because that is
 * the regime the product was first written against and no rates at all is worse
 * than the wrong ones: with none, `spendable` silently becomes the pre-tax
 * figure. They were nonetheless unreadable and unchangeable from anywhere in
 * the product, so a household anywhere else was shown a Danish answer it had no
 * way to recognise as one.
 *
 * Offered only to a household that holds something through a company. You would
 * not go looking for the rate on taking money out of a company before you had
 * one, and a rate editor for a situation the reader is not in is a question
 * they have to work out the irrelevance of before they can ignore it. Nothing
 * is lost by waiting: the rates have defaults, nothing else leads here, and the
 * card appears the moment the company does.
 */
export function TaxRatesPanel(): ReactElement | null {
  const rates = useTaxRates()
  const settings = useSettings()
  const save = useSaveTaxRates()
  const { settings: privateHoldings } = usePrivateHoldings()
  const holdsCompany = useHoldsThroughCompany(privateHoldings)
  const [drafts, setDrafts] = useState<TaxRateDraft[] | null>(null)

  // Absent rather than false until the answer arrives: a card that appears and
  // then withdraws reads as a fault, and this one is the page's first.
  if (holdsCompany !== true) return null

  const stored = (rates.data?.versions ?? []).map(draftFromVersion)
  const shown = drafts ?? stored
  const currency = currencyFrom(settings.data?.settings)
  const inForce = rates.data?.effective?.effectiveFrom
  const refusal = refuseTaxRateDrafts(shown)
  const edited = drafts !== null && JSON.stringify(drafts) !== JSON.stringify(stored)
  const busy = rates.isPending || save.isPending

  const edit = (index: number, next: TaxRateDraft): void =>
    setDrafts(shown.map((draft, at) => (at === index ? next : draft)))

  const commit = (): void => {
    if (refusal !== undefined) return
    save.mutate(sortDrafts(shown).map(toTaxRateVersion), { onSuccess: () => setDrafts(null) })
  }

  return (
    <Card className="nw-tax">
      <style>{CSS}</style>
      <CardHead
        id="nw-tax-heading"
        title="Tax on taking money out of a company"
        lede="These are this household's rates, not a rule of the world. A household is created holding Danish ones because those are the rates this was first built against; change them to whatever the household is actually charged, and Spendable follows on every page."
      />

      {rates.error && <p className="nw-error">Could not read the rates: {rates.error.message}</p>}
      {save.error && <p className="nw-error">Could not save: {save.error.message}</p>}

      <p className="nw-lede">
        A version applies from its date until the next one starts. Nothing applies before the
        earliest date below: a figure dated before it has no rates to use at all and is shown
        before tax, so history that reaches further back needs that date moved back with it. The
        threshold is an amount, counted in the same currency as every other figure here.
      </p>

      {!rates.isPending && shown.length === 0 && (
        <p className="nw-lede">
          No rates are configured. Nothing is subtracted anywhere for getting a company's money
          out, so Spendable is the figure before tax rather than the honest one.
        </p>
      )}

      {shown.length > 0 && (
        <ul>
          {shown.map((draft, index) => (
            <VersionRow
              key={index}
              draft={draft}
              currency={currency}
              inForce={draft.effectiveFrom !== '' && draft.effectiveFrom === inForce}
              disabled={busy}
              onChange={(next) => edit(index, next)}
              onRemove={() => setDrafts(shown.filter((_, at) => at !== index))}
            />
          ))}
        </ul>
      )}

      <DashedButton
        disabled={busy}
        onClick={() => setDrafts([...shown, nextDraftAfter(shown[shown.length - 1])])}
      >
        Add a version
      </DashedButton>

      <div className="nw-decide" style={{ marginTop: 'var(--nw-space-3)' }}>
        <Button kind="primary" disabled={busy || !edited || refusal !== undefined} onClick={commit}>
          {save.isPending ? 'Saving…' : 'Save rates'}
        </Button>
        {refusal !== undefined && <p className="nw-error">{refusal}</p>}
        {refusal === undefined && edited && (
          <p className="nw-unsaved">Not saved yet — no figure has moved.</p>
        )}
      </div>

      {rates.data !== undefined && <p className="nw-disclaimer">{rates.data.disclaimer}</p>}
    </Card>
  )
}

function VersionRow({
  draft,
  currency,
  inForce,
  disabled,
  onChange,
  onRemove,
}: {
  draft: TaxRateDraft
  /** Absent until the household has said what its figures are stated in. */
  currency: string | undefined
  inForce: boolean
  disabled: boolean
  onChange: (next: TaxRateDraft) => void
  onRemove: () => void
}): ReactElement {
  const named = draft.effectiveFrom === '' ? 'a new version' : draft.effectiveFrom

  return (
    <li>
      <div className="nw-version-head">
        <label>
          <span>In force from</span>
          <input
            type="date"
            value={draft.effectiveFrom}
            disabled={disabled}
            aria-label={`Date the rates of ${named} take effect`}
            onChange={(event) => onChange({ ...draft, effectiveFrom: event.target.value })}
          />
        </label>
        {inForce && <StatusPill tone="live" label="In force today" />}
        <Button disabled={disabled} onClick={onRemove}>
          Remove
        </Button>
      </div>

      <div className="nw-fields">
        <Percent
          label="Corporate gains"
          of={named}
          value={draft.corporateGainsPercent}
          disabled={disabled}
          onChange={(corporateGainsPercent) => onChange({ ...draft, corporateGainsPercent })}
        />
        <Percent
          label="Dividend, lower"
          of={named}
          value={draft.dividendLowPercent}
          disabled={disabled}
          onChange={(dividendLowPercent) => onChange({ ...draft, dividendLowPercent })}
        />
        <Percent
          label="Dividend, upper"
          of={named}
          value={draft.dividendHighPercent}
          disabled={disabled}
          onChange={(dividendHighPercent) => onChange({ ...draft, dividendHighPercent })}
        />
        <label className="nw-threshold">
          <span>Upper band above</span>
          <div className="nw-unit">
            <input
              type="number"
              min={0}
              step={0.01}
              value={draft.dividendThresholdMajor}
              disabled={disabled}
              aria-label={`Amount the upper dividend band starts above, from ${named}`}
              onChange={(event) =>
                onChange({ ...draft, dividendThresholdMajor: event.target.value })
              }
            />
            {currency !== undefined && <span aria-hidden="true">{currency}</span>}
          </div>
        </label>
      </div>
    </li>
  )
}

function Percent({
  label,
  of,
  value,
  disabled,
  onChange,
}: {
  label: string
  of: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}): ReactElement {
  return (
    <label>
      <span>{label}</span>
      <div className="nw-unit">
        <input
          type="number"
          min={0}
          max={100}
          step={0.1}
          value={value}
          disabled={disabled}
          aria-label={`${label} rate from ${of}, in percent`}
          onChange={(event) => onChange(event.target.value)}
        />
        <span aria-hidden="true">%</span>
      </div>
    </label>
  )
}

/**
 * A household is created holding a base currency now, so this is usually
 * present — but the card still says in words what the threshold is counted in
 * rather than relying on the code being there, because a settings read that has
 * not landed yet is indistinguishable from one that came back empty. Naming a
 * fallback here instead would compile a Danish default back into the browser.
 */
function currencyFrom(settings: Readonly<Record<string, unknown>> | undefined): string | undefined {
  const stored = settings?.['baseCurrency']
  return typeof stored === 'string' && stored !== '' ? stored.toUpperCase() : undefined
}
